import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IRecord } from '@platform/core/types';
import { usePatchRecordMutation } from '@platform/state/api';
import { ErrorBanner, Spinner } from '@platform/ui';
import { AddExistingChild } from './AddExistingChild';
import { childOrder } from './childOrder';
import { ChildRecordRow } from './ChildRecordRow';
import { childSummary } from './childSummary';
import { addFreshChild, freshChildrenOf } from './freshChildren';
import type { IChildRecordsTabProps } from './IChildRecordsTabProps';
import { NewChildRow } from './NewChildRow';
import { useChildRecords } from './useChildRecords';

/**
 * Вкладка «Дочірні завдання» (UIUX forms/child-records, прототип v4): панель «+ Нове дочірнє завдання» (новий рядок на місці зверху
 * списку — NewChildRow; створюється з батьком, напрямом та ітерацією) · «+ Додати наявне завдання» · підсумок «N · виконано · год
 * залишилось», зелена смуга поступу 6px і рядки дочірніх (щойно створені на місці — угорі, як у прототипі). Вилучення лише розриває
 * ієрархію — запис не видаляється.
 */
export function ChildRecordsTab({ ws, module, rt, record }: IChildRecordsTabProps) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [fresh, setFresh] = useState(() => freshChildrenOf(record.id));
  const { parent, items, isLoading, error } = useChildRecords(ws, rt, record);
  const [patch, { error: patchError }] = usePatchRecordMutation();
  const canEdit = record.permissions?.edit !== false;
  const sum = childSummary(items, rt.statuses);
  const isTask = rt.key === 'task';
  const setParent = (child: IRecord, value: string | null) => {
    if (parent) void patch({ ws, rt: rt.key, id: child.id, body: { values: { [parent.key]: value } }, rowVersion: child.rowVersion });
  };
  return (
    <div className="rtab-children">
      <div className="rtab-bar" role="toolbar" aria-label={isTask ? t('recordPage.tabs.childTasks') : t('recordPage.tabs.children')}>
        {canEdit && (
          <button type="button" className="rtab-btn rtab-btn--primary rtab-btn--flat" onClick={() => setAdding(true)}>
            {isTask ? t('recordPage.children.new') : t('recordPage.children.newOf')}
          </button>
        )}
        {canEdit && <AddExistingChild ws={ws} rt={rt} parent={record} children={items} onPick={(r) => setParent(r, record.id)} />}
        <span className="rtab-bar__spacer" />
        <span className="rtab-bar__summary">{t('recordPage.children.summary', { count: sum.count, done: sum.done, hours: sum.hours })}</span>
      </div>
      <div className="rtab-progress" role="progressbar" aria-label={t('recordPage.children.progress')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={sum.percent}>
        <span style={{ width: `${sum.percent}%` }} />
      </div>
      <ErrorBanner error={error ?? patchError} />
      <div className="rtab-box rtab-box--children">
        {adding && parent && (
          <NewChildRow ws={ws} rt={rt} parent={record} parentKey={parent.key} onDone={(c) => { if (c) setFresh(addFreshChild(record.id, c.id)); setAdding(false); }} />
        )}
        {isLoading ? <div className="rtab-empty"><Spinner /></div> : items.length
          ? childOrder(items, fresh).map((c) => <ChildRecordRow key={c.id} moduleKey={module.module.key} rt={rt} child={c} canEdit={canEdit} onRemove={(x) => setParent(x, null)} />)
          : !adding && <p className="rtab-empty">{t('recordPage.children.empty')}</p>}
      </div>
    </div>
  );
}
